import { Button } from '@askable/ui/core/button';
import { cn } from '@askable/ui/lib/utils';
import { CircleCheck, Lock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'src/lib/routing';

import { GuestLockedPopover } from 'components/common/GuestLockedPopover';
import { LockedPopover, useLockedPopover } from 'components/common/LockedPopover';
import { analytics } from 'lib/analytics';

import type { IconProps } from '@askable/ui/illustrations';

const menuItemClass = `flex w-full cursor-pointer select-none items-center gap-3 rounded-md p-3 text-base outline-none
  transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground
  data-[disabled]:pointer-events-none data-[disabled]:opacity-50`;

type Side = 'right' | 'left';

export type StudyOption = {
  description: string;
  href?: string;
  icon: React.ComponentType<IconProps>;
  newTab?: boolean;
  status?: 'upgrade_required' | 'coming_soon' | 'guest_locked' | 'team_locked';
  side?: Side;
  title: string;
  onClick?: () => void;
};

export const UpgradePopover = ({ isPopoverOpen, side }: { isPopoverOpen: boolean; side?: Side }) => {
  const { t } = useTranslation();
  const [isEventSent, setIsEventSent] = useState(false);

  const handleNotifyEventClick = () => {
    void analytics.track('client.upgrade_to_tracks', {
      page: window.location.href,
      action: 'Unmoderated Study clicked',
    });
    setIsEventSent(true);
  };

  return (
    <LockedPopover isOpen={isPopoverOpen} side={side} className="w-52 p-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 text-sm">
          <div className="font-semibold leading-none">{t('sections.createStudy.upgrade.title')}</div>
          <div className="text-balance text-muted-foreground">{t('sections.createStudy.upgrade.description')}</div>
        </div>

        {isEventSent ? (
          <div className="flex h-8 gap-2 text-sm">
            <CircleCheck className="h-4 w-4 shrink-0 text-success" />
            {t('sections.createStudy.upgrade.success')}
          </div>
        ) : (
          <Button variant="outline" onClick={handleNotifyEventClick}>
            {t('global.learnMore')}
          </Button>
        )}
      </div>
    </LockedPopover>
  );
};

const MenuItemContent = ({
  description,
  icon: Icon,
  status,
  title,
}: Pick<StudyOption, 'title' | 'description' | 'status' | 'icon'>) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex-0">
        <Icon size={24} />
      </div>
      <div className="flex flex-col text-sm">
        <div className="flex items-center gap-2 font-semibold">
          {title}
          {status === 'upgrade_required' || status === 'guest_locked' || status === 'team_locked' ? (
            <Lock className="h-3 w-3" />
          ) : null}
          {status === 'coming_soon' ? (
            <div className="whitespace-nowrap rounded-sm bg-foreground px-1 py-0 text-xs font-semibold leading-4 text-background">
              {t('sections.studies.comingSoon')}
            </div>
          ) : null}
        </div>
        <div className="text-muted-foreground">{description}</div>
      </div>
    </>
  );
};

export const CreateStudyItem = ({
  title,
  description,
  href,
  icon,
  status,
  side,
  onClick,
}: Omit<StudyOption, 'isVisible'>) => {
  const { t } = useTranslation();
  const { isOpen: isPopoverOpen, toggle: togglePopover, close: closePopover } = useLockedPopover();
  const menuContent = (
    <MenuItemContent key={title} title={title} description={description} status={status} icon={icon} />
  );

  const isUpgradeRequired = status === 'upgrade_required';
  const isComingSoon = status === 'coming_soon';
  const isGuestLocked = status === 'guest_locked';
  const isTeamLocked = status === 'team_locked';
  const hasOnClick = Boolean(onClick);

  if (isGuestLocked) {
    return (
      <GuestLockedPopover description={t('sections.createStudy.guest.createStudyDescription')} side={side}>
        <button
          type="button"
          aria-disabled="true"
          className={cn(menuItemClass, 'cursor-not-allowed items-start')}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {menuContent}
        </button>
      </GuestLockedPopover>
    );
  }

  if (isTeamLocked) {
    return (
      <div className={cn(menuItemClass, 'cursor-not-allowed items-start opacity-100')} aria-disabled="true">
        {menuContent}
      </div>
    );
  }

  if (isUpgradeRequired) {
    return (
      <button
        type="button"
        className={cn(menuItemClass, 'group relative items-start')}
        onMouseEnter={closePopover}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          togglePopover();
        }}
      >
        {menuContent}
        <UpgradePopover isPopoverOpen={isPopoverOpen} side={side} />
      </button>
    );
  }

  if (isComingSoon) {
    return (
      <div className={cn(menuItemClass, 'cursor-not-allowed items-start opacity-100')} aria-disabled="true">
        {menuContent}
      </div>
    );
  }

  if (hasOnClick) {
    return (
      <button
        type="button"
        className={cn(menuItemClass, 'items-start')}
        onClick={e => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        {menuContent}
      </button>
    );
  }

  return (
    <Link to={href ?? '/'} reloadDocument className={cn(menuItemClass, 'items-start')}>
      {menuContent}
    </Link>
  );
};
